/**
 * Profile Completion Utility
 *
 * This module provides a centralized, reusable function to check if a user's profile is complete.
 * This ensures consistent profile completion validation across the entire application.
 */

import { Profile } from './supabase';

/**
 * Checks if a user profile is complete with all required fields.
 *
 * Required fields:
 * - job_title: Must be a non-empty string (after trimming whitespace)
 * - company: Must be a non-empty string (after trimming whitespace)
 *
 * @param profile - The profile object to check (can be null)
 * @returns true if profile has all required fields filled, false otherwise
 */
export function isProfileComplete(profile: Profile | null): boolean {
  // No profile means incomplete
  if (!profile) {
    return false;
  }

  // Check job_title: must exist, not be null, not be undefined, and not be empty after trimming
  const hasJobTitle =
    profile.job_title !== null &&
    profile.job_title !== undefined &&
    typeof profile.job_title === 'string' &&
    profile.job_title.trim().length > 0;

  // Check company: must exist, not be null, not be undefined, and not be empty after trimming
  const hasCompany =
    profile.company !== null &&
    profile.company !== undefined &&
    typeof profile.company === 'string' &&
    profile.company.trim().length > 0;

  const isComplete = hasJobTitle && hasCompany;

  // Log for debugging purposes
  console.log('[Profile Utils] isProfileComplete check:', {
    profileExists: !!profile,
    profileId: profile?.id,
    hasJobTitle,
    jobTitle: profile?.job_title,
    hasCompany,
    company: profile?.company,
    result: isComplete,
  });

  return isComplete;
}

/**
 * Fetches a fresh profile from the database and checks if it's complete.
 * This is useful when you need to verify profile completion with the latest data.
 *
 * @param supabase - The Supabase client instance
 * @param userId - The user ID to fetch the profile for
 * @returns Object containing the profile and whether it's complete
 */
export async function fetchAndCheckProfile(
  supabase: any,
  userId: string
): Promise<{ profile: Profile | null; isComplete: boolean }> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[Profile Utils] Error fetching profile:', error);
      return { profile: null, isComplete: false };
    }

    const isComplete = isProfileComplete(data);

    console.log('[Profile Utils] fetchAndCheckProfile result:', {
      userId,
      hasProfile: !!data,
      isComplete,
    });

    return { profile: data, isComplete };
  } catch (error) {
    console.error('[Profile Utils] Exception fetching profile:', error);
    return { profile: null, isComplete: false };
  }
}
